import { Injectable } from '@angular/core';
import { UserService } from 'src/app/services/user-service/userservice';
import { getLocalStorageItem } from 'src/app/utils/browser-storage';

interface KeystoreJwtPayload {
  authorities?: string[] | string;
  organisation_roles?: Record<string, string[] | string>;
}

@Injectable({ providedIn: 'root' })
export class KeystoreAccessService {
  constructor(private readonly userService: UserService) { }

  hasAdminRole(organisationId: number): boolean {
    return this.userService.isUserAdminOfOrganisationOrPlattformAdmin(organisationId)
      || this.hasOrganisationRole(organisationId, 'Keystore-Admin');
  }

  hasKeyGenerationPermission(organisationId: number): boolean {
    return this.userService.isUserAdminOfOrganisationOrPlattformAdmin(organisationId)
      || this.hasOrganisationRole(organisationId, 'Keystore-Admin');
  }

  hasReadRole(organisationId: number): boolean {
    return this.hasAdminRole(organisationId)
      || this.hasOrganisationRole(organisationId, 'Keystore-Read');
  }

  private hasOrganisationRole(organisationId: number, role: string): boolean {
    const payload = this.readAccessTokenPayload();
    if (!payload) {
      return false;
    }
    const authorities = Array.isArray(payload.authorities)
      ? payload.authorities
      : payload.authorities
        ? [payload.authorities]
        : [];
    if (authorities.includes('PLATTFORMADMIN') || authorities.includes('ROLE_PLATTFORMADMIN')) return true;
    const roles = payload.organisation_roles?.[String(organisationId)];
    return (Array.isArray(roles) ? roles : roles ? [roles] : []).includes(role);
  }

  private readAccessTokenPayload(): KeystoreJwtPayload | null {
    const token = getLocalStorageItem('access_token');
    if (!token) {
      return null;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    try {
      const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const binary = globalThis.atob(padded);
      const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes)) as KeystoreJwtPayload;
    } catch {
      return null;
    }
  }
}
